import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  IonItem,
  IonList,
  IonSpinner,
  IonButton,
  IonNote,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';

import { saveEmployee } from '@/actions/employees';
import { preventDefault } from '@/helpers/events';
import { validName, validEmail, validPhoneNumber } from '@/helpers/validators';
import ValidatedInput from '@/components/ValidatedInput';

export default function EmployeeForm({employee={}, done=_.noop}) {
  const dispatch = useDispatch();
  const user = useSelector((store) => store.user);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [name, setName] = useState(employee.name ?? "");
  const [email, setEmail] = useState(employee.email ?? "");
  const [phoneNumber, setPhoneNumber] = useState(employee.phone_number ?? "");
  const [role, setRole] = useState(employee.role ?? "attendant");

  const emailValid = validEmail(email);
  const nameValid = validName(name);
  const phoneNumberValid = validPhoneNumber(phoneNumber);

  const valid = _.every([emailValid, nameValid, phoneNumberValid]);

  const canSetRole = user.role != 'attendant' && user.uuid != employee.uuid;

  const submitEmployee = async () => {
    setErrorMessage(null);
    if (!valid) return;

    setSaving(true);
    try {
      await dispatch(saveEmployee({
        uuid: employee.uuid,
        name,
        email,
        phoneNumber,
        ...(canSetRole ? {role} : {})
      }));
      setSaving(false);
      done(true);
    } catch (e) {
      setSaving(false);
      setErrorMessage(_.get(e, 'response.data.error', e.message));
    }
  };

  return (
    <form onSubmit={preventDefault(submitEmployee)}>
      {errorMessage && <IonItem color="danger">{errorMessage}</IonItem>}
      <IonList lines="none">
        <IonItem lines="none">
          <ValidatedInput label="Name"
            name="name"
            value={name}
            setValue={setName}
            valid={nameValid}
            errorText="Enter a first and last name"
            required/>
        </IonItem>
        <IonItem lines="none">
          <ValidatedInput label="Phone Number"
            name="phoneNumber"
            value={phoneNumber}
            setValue={setPhoneNumber}
            valid={phoneNumberValid}
            errorText="Phone number must contain at least 10 digits"
            required/>
        </IonItem>
        <IonItem lines="none">
          <ValidatedInput label="Email"
            type="email"
            name="email"
            value={email}
            setValue={setEmail}
            valid={emailValid}
            errorText="Enter a valid email"
            required/>
        </IonItem>
        {canSetRole && (
          <IonItem>
            <IonSelect label="Role"
              labelPlacement="floating"
              name="role"
              value={role}
              setValue={setRole}
              interface="popover"
            >
              <IonSelectOption value="attendant">Attendant</IonSelectOption>
              <IonSelectOption value="manager">Manager</IonSelectOption>
              {user.role == 'owner' && (
                <IonSelectOption value="owner">Owner</IonSelectOption>
              )}
            </IonSelect>
          </IonItem>
        )}
        <div className="ion-padding">
          <IonButton type="submit" expand="block">
            {saving ? <IonSpinner name="dots"/> : `${employee.uuid ? 'Update' : 'Add'} Employee`}
          </IonButton>
          {!employee.uuid && (
            <IonNote>
              An invitation will be sent by email to this employee with instructions
              on how to set their password and log in.
            </IonNote>
          )}
        </div>
      </IonList>
    </form>
  );
}
